import React, { useMemo } from 'react';
import { Card, Descriptions, Divider, Typography, Alert, Spin, Skeleton } from 'antd';
import { InfoCircleOutlined, DollarOutlined, CalculatorOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useLoanFeeCalculation, formatFeeAmount, formatFeeDescription, getFeeTimingText } from '../../hooks/useLoanFeeCalculation';
import {FeeDetail} from "@/shared/api/model";

const { Title, Text } = Typography;

interface LoanFeeBreakdownProps {
  glNo?: string;
  loanAmount?: number;
  loanPeriod?: number;
  showTitle?: boolean;
  compact?: boolean;
  loanBalanceData?: any; // For top-up/refinance loan calculations
  netAmountLabel?: string; // Custom label for net amount (default: "Net Amount You'll Receive")
  customerReceiveAmount?: number;
}

/**
 * Enhanced component for displaying detailed loan fee breakdown
 */
export const LoanFeeBreakdown: React.FC<LoanFeeBreakdownProps> = ({
                                                                    glNo,
                                                                    loanAmount,
                                                                    loanPeriod,
                                                                    showTitle = true,
                                                                    compact = false,
                                                                    loanBalanceData,
                                                                    netAmountLabel = "Net Amount You'll Receive",
                                                                    customerReceiveAmount,
                                                                  }) => {
  const { data: feeCalculation, isLoading, error, isError } = useLoanFeeCalculation(
    glNo, loanAmount, loanPeriod, loanBalanceData
  );

  if (isLoading) {
    return (
        <Card size="small">
          <Skeleton active paragraph={{ rows: 3 }} />
        </Card>
    );
  }

  if (isError || !feeCalculation || !feeCalculation.data) {
    return (
        <Alert
            message="Fee Calculation Unavailable"
            description={error instanceof Error ? error.message : "Unable to calculate fees for this loan product"}
            type="warning"
            icon={<InfoCircleOutlined />}
            showIcon
        />
    );
  }

  const {
    disbursementFees,
    repaymentFees,
    totalDisbursementFees,
    totalRepaymentFees,
    netDisbursementAmount,
    hasDisbursementFees,
    hasRepaymentFees
  } = feeCalculation.data || {};

  // Calculate settlement amount for top-up/refinance loans
  let settlementAmount = 0;
  try {
    if (loanBalanceData && loanBalanceData.activeLoanBehavior) {
      if (loanBalanceData.activeLoanBehavior === 'ALLOW_TOPUP' ||
          loanBalanceData.activeLoanBehavior === 'ALLOW_REFINANCE') {
        const unpaidPrincipal = Number(loanBalanceData.unpaidPrincipal) || 0;
        const unpaidAccruedInterest = loanBalanceData.includeAccruedInterest ?
            (Number(loanBalanceData.unpaidAccruedInterest) || 0) : 0;
        settlementAmount = unpaidPrincipal + unpaidAccruedInterest;
      }
    }
  } catch (error) {
    settlementAmount = 0;
  }

  // Calculate actual net amount customer will receive (after fees and settlement)
  let actualNetAmount = 0;
  try {
    const netAmount = Number(netDisbursementAmount) || 0;
    const settlement = Number(settlementAmount) || 0;
    actualNetAmount = netAmount - settlement;
  } catch (error) {
    actualNetAmount = Number(netDisbursementAmount) || 0;
  }

  // Determine if this is a top-up or refinance loan
  let isTopUpOrRefinance = false;
  try {
    isTopUpOrRefinance = loanBalanceData?.activeLoanBehavior === 'ALLOW_TOPUP' ||
        loanBalanceData?.activeLoanBehavior === 'ALLOW_REFINANCE';
  } catch (error) {
    isTopUpOrRefinance = false;
  }

  const renderFeeList = (fees: FeeDetail[], title: string) => {
    if (!fees || fees.length === 0) return null;

    return (
        <div style={{ marginBottom: 16 }}>
          <Text strong style={{ fontSize: '14px', color: '#1890ff' }}>
            {title}
          </Text>
          <div style={{ marginTop: 8 }}>
            {fees.map((fee, index) => (
                <div key={index} style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  padding: '12px',
                  backgroundColor: '#fafafa',
                  borderRadius: '4px',
                  marginBottom: '8px',
                  border: '1px solid #f0f0f0'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                    <Text strong style={{ flex: 1, fontSize: '13px' }}>{fee.feeName || 'Fee'}</Text>
                    <Text strong style={{ color: '#f5222d', fontSize: '14px', whiteSpace: 'nowrap' }}>
                      {formatFeeAmount(fee.calculatedAmount || 0)}
                    </Text>
                  </div>
                  <Text type="secondary" style={{ fontSize: '11px', lineHeight: '1.4' }}>
                    {formatFeeDescription(fee)} • {getFeeTimingText(fee.collectionTiming)}
                  </Text>
                </div>
            ))}
          </div>
        </div>
    );
  };

  const renderCompactView = () => (
      <Card size="small" style={{ marginBottom: 16 }} bodyStyle={{ padding: '8px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text style={{ fontSize: '13px' }}>Loan Amount</Text>
            <Text strong style={{ fontSize: '15px' }}>
              {formatFeeAmount(loanAmount || 0)}
            </Text>
          </div>

          {hasDisbursementFees && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text type="secondary" style={{ fontSize: '13px' }}>Less Fees</Text>
                <Text strong style={{ color: '#f5222d', fontSize: '14px' }}>
                  -{formatFeeAmount(totalDisbursementFees || 0)}
                </Text>
              </div>
          )}

          <Divider style={{ margin: '4px 0' }} />
          
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text style={{ fontSize: '13px' }}>{netAmountLabel.includes('Customer') ? 'Customer Receives' : 'You Receive'}</Text>
            <Text strong style={{ fontSize: '16px', color: '#52c41a' }}>
              {formatFeeAmount(customerReceiveAmount ?? (isTopUpOrRefinance ? actualNetAmount : (netDisbursementAmount || 0)))}
            </Text>
          </div>
        </div>
      </Card>
  );

  const renderDetailedView = () => (
      <Card
          title={showTitle ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }} className="sm:flex-row sm:gap-2">
                <CalculatorOutlined style={{ fontSize: '20px' }} />
                <span>Fee Breakdown</span>
              </div>
          ) : undefined}
          size="small"
          style={{ marginBottom: 16 }}
          bodyStyle={{ padding: '12px 8px' }}
      >
        {/* Disbursement Type Information */}
        {loanBalanceData && isTopUpOrRefinance && (
            <Alert
                message={
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, textAlign: 'center' }} className="sm:flex-row sm:text-left">
                    <InfoCircleOutlined style={{ fontSize: '18px' }} />
                    <span>{`${loanBalanceData.activeLoanBehavior === 'ALLOW_TOPUP' ? 'Top-up' : 'Refinance'} Loan Disbursement`}</span>
                  </div>
                }
                description={`This is a ${loanBalanceData.activeLoanBehavior === 'ALLOW_TOPUP' ? 'top-up' : 'refinance'} loan. Part of the loan amount will be used to settle your existing loan balance.`}
                type="info"
                showIcon={false}
                style={{ marginBottom: 16, fontSize: '12px' }}
            />
        )}

        {loanBalanceData && !isTopUpOrRefinance && loanBalanceData.activeLoanBehavior && (
            <Alert
                message={
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, textAlign: 'center' }} className="sm:flex-row sm:text-left">
                    <CheckCircleOutlined style={{ fontSize: '18px', color: '#52c41a' }} />
                    <span>Regular Loan Disbursement</span>
                  </div>
                }
                description="This is a regular loan disbursement. The full net amount will be transferred to your account."
                type="success"
                showIcon={false}
                style={{ marginBottom: 16, fontSize: '12px' }}
            />
        )}
        {/* Loan Summary */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
            <Text style={{ fontSize: '13px' }}>Requested Amount</Text>
            <Text strong style={{ fontSize: '15px' }}>
              {formatFeeAmount(loanAmount || 0)}
            </Text>
          </div>

          {hasDisbursementFees && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                <Text style={{ fontSize: '13px' }}>Total Disbursement Fees</Text>
                <Text strong style={{ color: '#f5222d', fontSize: '14px' }}>
                  -{formatFeeAmount(totalDisbursementFees || 0)}
                </Text>
              </div>
          )}

          {isTopUpOrRefinance && settlementAmount > 0 && (
              <div style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                  <Text style={{ fontSize: '13px' }}>Settlement Amount</Text>
                  <Text strong style={{ color: '#f5222d', fontSize: '14px' }}>
                    -{formatFeeAmount(settlementAmount)}
                  </Text>
                </div>
                <div style={{ fontSize: '11px', color: '#666', paddingLeft: '4px' }}>
                  Unpaid Principal: {formatFeeAmount(loanBalanceData?.unpaidPrincipal || 0)}
                  {loanBalanceData?.includeAccruedInterest && loanBalanceData?.unpaidAccruedInterest > 0 && (
                      <span>, Accrued Interest: {formatFeeAmount(loanBalanceData.unpaidAccruedInterest)}</span>
                  )}
                </div>
              </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', backgroundColor: '#f6ffed', marginTop: '8px', paddingLeft: '8px', paddingRight: '8px', borderRadius: '4px' }}>
            <Text strong style={{ fontSize: '13px' }}>{netAmountLabel}</Text>
            <Text strong style={{ fontSize: '18px', color: '#52c41a' }}>
              {formatFeeAmount(customerReceiveAmount ?? (isTopUpOrRefinance ? actualNetAmount : (netDisbursementAmount || 0)))}
            </Text>
          </div>
        </div>

        {/* Fee Details */}
        {hasDisbursementFees && (
            <>
              <Divider style={{ margin: '16px 0' }} />
              {renderFeeList(disbursementFees || [], 'Disbursement Fees (Deducted from loan amount)')}
            </>
        )}

        {hasRepaymentFees && (
            <>
              <Divider style={{ margin: '16px 0' }} />
              {renderFeeList(repaymentFees || [], 'Repayment Fees (Collected during repayment)')}
            </>
        )}

        {/* No Fees Message */}
        {!hasDisbursementFees && !hasRepaymentFees && (
            <Alert
                message="No Additional Fees"
                description="This loan product has no additional fees. You will receive the full loan amount."
                type="success"
                icon={<InfoCircleOutlined />}
                showIcon
                style={{ marginTop: 16 }}
            />
        )}

        {/* Important Notice */}
        {hasDisbursementFees && (
            <Alert
                message="Important Notice"
                description="Disbursement fees will be deducted from customers loan amount before funds are transferred to customer account."
                type="info"
                icon={<InfoCircleOutlined />}
                showIcon
                style={{ marginTop: 16 }}
            />
        )}
      </Card>
  );

  return compact ? renderCompactView() : renderDetailedView();
};

export default LoanFeeBreakdown;
